import { Icon, IconName, ProductColor, colors } from '@aloft/toolkit-sui';

import toast from 'react-hot-toast';

import { openCraftsmanAlerts } from '../../utils/openCraftsmanAlerts.ts';
import { RenderToast } from './RenderToast.tsx';
import {
  COMPARABLES_ALERTS_TOAST_CLASS_NAME,
  TOAST_CLASS_NAME,
  TOAST_DURATION_IN_MS,
  TOAST_PERSISTENT_DURATION_IN_MS,
} from './constants.ts';
import { dismissAllToasts, registerAlertToast } from './dismissAllToasts.ts';
import { MakeToast, ToastType } from './types.ts';

const toastTypeToIconProps: {
  [key in ToastType]: {
    icon: IconName;
    color: ProductColor;
  };
} = {
  warning: {
    icon: 'error-warning',
    color: 'alertWarning',
  },
  info: {
    icon: 'information',
    color: 'surfaceAccent6',
  },
  success: {
    icon: 'check-circle',
    color: 'success',
  },
  error: {
    icon: 'close-circle',
    color: 'alertError',
  },
};

const toastTypeToStyleProps: {
  [key in ToastType]: React.CSSProperties;
} = {
  warning: {
    backgroundColor: colors.alertWarningLight,
    borderColor: colors.alertWarning,
  },
  info: {
    backgroundColor: colors.surface3,
    borderColor: colors.surfaceAccent6,
  },
  success: {
    backgroundColor: colors.alertSuccessLight,
    borderColor: colors.success,
  },
  error: {
    backgroundColor: colors.alertErrorLight,
    borderColor: colors.alertError,
  },
};

const makeReactHotToast: MakeToast = ({
  id,
  type,
  title,
  body,
  action,
  compType,
  onClick,
  openAlertsOnClick,
  singleSlice = true,
  durationInMs = TOAST_DURATION_IN_MS,
  position = 'bottom-right',
  comparablesAlerts = false,
}) => {
  const iconProps = toastTypeToIconProps[type];
  const styleProps = toastTypeToStyleProps[type];
  const isClickable = Boolean(onClick ?? openAlertsOnClick);
  const isAlertToast = comparablesAlerts || openAlertsOnClick;

  const toastPosition = isAlertToast ? 'top-right' : position;

  const toastId = toast(
    (t) =>
      RenderToast({
        title,
        body,
        compType,
        action,
        isClickable,
        onClose: () => toast.dismiss(t.id),
        onClick: isClickable
          ? () => {
              onClick?.();
              if (openAlertsOnClick) {
                openCraftsmanAlerts();
              } else {
                dismissAllToasts();
              }
            }
          : undefined,
      }),
    {
      id: singleSlice ? id : undefined,
      duration: isAlertToast ? TOAST_PERSISTENT_DURATION_IN_MS : durationInMs,
      position: toastPosition,
      style: {
        border: '1px solid',
        maxWidth: '600px',
        ...styleProps,
      },
      className: [TOAST_CLASS_NAME, isAlertToast ? COMPARABLES_ALERTS_TOAST_CLASS_NAME : undefined]
        .filter(Boolean)
        .join(' '),
      icon: <Icon size="lg" {...iconProps} />,
    },
  );

  if (isAlertToast) {
    registerAlertToast(toastId);
  }
};

export const useToaster = () => makeReactHotToast;
